import { Component } from '@theme/component';

class HeroSlideshowComponent extends Component {
  #current = 0;
  #timer = 0;
  #inView = true;
  #held = false;
  #autoplay = 0;
  #touchX = 0;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.#autoplay = Number(this.dataset.autoplay) * 1000;

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#inView = Boolean(entry?.isIntersecting);
      this.#update();
    });
    this.#observer.observe(this);

    document.addEventListener('visibilitychange', this.#update);
    this.addEventListener('focusin', this.#hold);
    this.addEventListener('focusout', this.#release);
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#release);

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#update);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#release);
  }

  get #slides() {
    return /** @type {HTMLElement[]} */ (this.refs.slides ?? []);
  }

  /**
   * @param {number} index
   */
  select(index) {
    const slides = this.#slides;
    if (!slides.length) return;

    this.#current = (index + slides.length) % slides.length;

    slides.forEach((slide, i) => {
      const active = i === this.#current;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });

    if (this.refs.counter) this.refs.counter.textContent = String(this.#current + 1).padStart(2, '0');

    this.#update();
  }

  previous() {
    this.select(this.#current - 1);
  }

  next() {
    this.select(this.#current + 1);
  }

  /**
   * @param {TouchEvent} event
   */
  #onTouchStart = (event) => {
    this.#touchX = event.touches[0]?.clientX ?? 0;
  };

  /**
   * @param {TouchEvent} event
   */
  #onTouchEnd = (event) => {
    const delta = (event.changedTouches[0]?.clientX ?? 0) - this.#touchX;
    if (Math.abs(delta) < 50) return;

    if (delta < 0) this.next();
    else this.previous();
  };

  #update = () => {
    clearTimeout(this.#timer);

    const running = this.#inView && !document.hidden;

    this.#slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;

      if (i === this.#current && running) {
        if (video.paused) video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
        if (i !== this.#current) video.currentTime = 0;
      }
    });

    const progress = this.refs.progress;
    if (progress) progress.style.animation = 'none';

    if (!running || this.#held || !this.#autoplay || this.#slides.length < 2) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#timer = setTimeout(() => this.next(), this.#autoplay);

    if (progress) {
      progress.getBoundingClientRect();
      progress.style.animation = `hero-slideshow-progress ${this.#autoplay}ms linear`;
    }
  };

  #hold = () => {
    this.#held = true;
    this.#update();
  };

  #release = () => {
    this.#held = false;
    this.#update();
  };

  /**
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const index = this.#slides.findIndex((slide) => slide.contains(/** @type {Node} */ (event.target)));
    if (index === -1) return;

    this.#held = true;
    this.select(index);
  };
}

if (!customElements.get('hero-slideshow-component')) {
  customElements.define('hero-slideshow-component', HeroSlideshowComponent);
}
